import { useRef, useState } from "react";
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  BuildingsIcon,
  CalendarBlankIcon,
  ClockIcon,
  CrosshairIcon,
  MapPinIcon,
  MapPinAreaIcon,
  RoadHorizonIcon,
  TreeIcon,
  WarningCircleIcon,
} from "@phosphor-icons/react";
import { sanitizarTexto } from "@encuentrayayuda/shared/lib/sanitizar";
import { esMomentoPasado } from "@encuentrayayuda/shared/lib/fechas";
import { LUGAR_ENCONTRADO } from "@encuentrayayuda/shared/schemas/persona";
import { MapaUbicacion } from "./UbicacionMascotaForm.jsx";
import { buscarDireccion, obtenerPosicionActual } from "./lib/geocodificacion.js";
import AvisoDireccionDesalineada from "./AvisoDireccionDesalineada.jsx";

const lugaresEncontrados = [
  { value: "HOSPITAL", label: "Hospital / Centro de salud", Icon: BuildingsIcon },
  { value: "VIA_PUBLICA", label: "Vía pública / Calle", Icon: RoadHorizonIcon },
  { value: "LUGAR_PUBLICO", label: "Lugar público (parque, plaza, etc.)", Icon: TreeIcon },
  { value: "OTRO", label: "Otro lugar", Icon: MapPinAreaIcon },
];

const camposLugar = {
  HOSPITAL: { label: "Nombre del hospital o centro de salud", placeholder: "Ej: Hospital San Vicente Fundación" },
  VIA_PUBLICA: { label: "Dirección", placeholder: "Ej: Calle 10 # 45-20" },
  LUGAR_PUBLICO: { label: "Nombre o referencia del lugar público", placeholder: "Ej: Parque principal, Plaza de Bolívar" },
  OTRO: { label: "Descripción del lugar", placeholder: "Ej: Entrada del conjunto, finca o establecimiento" },
};

export default function UbicacionPersonaForm({ datos, onChange, onBack, onNext, onMissing, esEncontrada = false }) {
  const [estadoGps, setEstadoGps] = useState("inactivo");
  const [mensajeGps, setMensajeGps] = useState("");
  const solicitudRef = useRef(0);

  function guardarPunto({ latitud, longitud, precision }) {
    onChange((actuales) => ({
      ...actuales,
      latEvento: Number(latitud.toFixed(7)),
      lngEvento: Number(longitud.toFixed(7)),
      precisionGps: precision ? Math.round(precision) : null,
    }));
    setEstadoGps("listo");
    setMensajeGps(precision ? `Ubicación obtenida con una precisión aproximada de ${Math.round(precision)} m.` : "Ubicación seleccionada en el mapa.");
  }

  async function completarDireccion(latitud, longitud) {
    const solicitud = solicitudRef.current + 1;
    solicitudRef.current = solicitud;
    try {
      setMensajeGps("Buscando la dirección del punto seleccionado…");
      const ubicacion = await buscarDireccion(latitud, longitud);
      if (solicitud !== solicitudRef.current) return;
      onChange((actuales) => ({
        ...actuales,
        direccionEvento: ubicacion.direccion,
        ciudad: ubicacion.ciudad || null,
        departamento: ubicacion.departamento || null,
      }));
      setMensajeGps("Ubicación y dirección obtenidas correctamente.");
    } catch {
      if (solicitud !== solicitudRef.current) return;
      setMensajeGps("El punto quedó guardado. Escribe una dirección o referencia para continuar.");
    }
  }

  function seleccionarPunto(punto) {
    guardarPunto(punto);
    completarDireccion(punto.latitud, punto.longitud);
  }

  function usarUbicacionActual() {
    setEstadoGps("cargando");
    setMensajeGps("Buscando tu ubicación…");
    obtenerPosicionActual()
      .then(seleccionarPunto)
      .catch(() => {
        setEstadoGps("error");
        setMensajeGps("No pudimos usar tu ubicación. Activa el permiso o marca el punto en el mapa.");
      });
  }

  function seleccionarLugar(lugarEncontrado) {
    if (!LUGAR_ENCONTRADO.includes(lugarEncontrado)) return;
    onChange((actuales) => ({ ...actuales, lugarEncontrado, nombreHospital: "" }));
  }

  function continuar(evento) {
    evento.preventDefault();
    const direccion = sanitizarTexto(datos.direccionEvento);
    const puntoReferencia = sanitizarTexto(datos.puntoReferencia);
    const faltantes = [];
    if (esEncontrada && !LUGAR_ENCONTRADO.includes(datos.lugarEncontrado)) faltantes.push("Tipo de lugar donde encontraste a la persona");
    if (!esMomentoPasado(datos.fechaEvento, datos.horaEvento)) faltantes.push("Fecha y hora que ya hayan ocurrido");
    if (datos.latEvento == null || datos.lngEvento == null) faltantes.push(esEncontrada ? "Lugar donde encontraste a la persona" : "Lugar donde fue vista por última vez");
    if (!direccion) faltantes.push("Dirección o referencia");
    onChange((actuales) => ({ ...actuales, direccionEvento: direccion, puntoReferencia }));
    if (faltantes.length) {
      onMissing(faltantes);
      return;
    }
    onNext();
  }

  return (
    <section className="person-location-step">
      <div className="person-location-step__intro"><h2>{esEncontrada ? "¿Dónde y cuándo la encontraste?" : "¿Dónde y cuándo fue vista por última vez?"}</h2><p>La ubicación y el momento son claves para {esEncontrada ? "protegerla y contactar a su familia." : "ayudar a encontrarla."}</p></div>

      <form className="person-location-form" onSubmit={continuar}>
        {esEncontrada && <>
          <fieldset className="person-found-place"><legend>Lugar donde está</legend><p>Puedes seleccionar un lugar específico</p><div>{lugaresEncontrados.map(({ value, label, Icon }) => <button type="button" className={datos.lugarEncontrado === value ? "is-selected" : ""} aria-pressed={datos.lugarEncontrado === value} onClick={() => seleccionarLugar(value)} key={value}><Icon weight="duotone" />{label}</button>)}</div></fieldset>
          {datos.lugarEncontrado && <label>{camposLugar[datos.lugarEncontrado].label} <span>(opcional)</span><input className="person-place-detail" type="text" maxLength="120" value={datos.nombreHospital} onChange={(evento) => onChange((actuales) => ({ ...actuales, nombreHospital: sanitizarTexto(evento.target.value, { recortar: false }) }))} placeholder={camposLugar[datos.lugarEncontrado].placeholder} /></label>}
        </>}

        <fieldset><legend>{esEncontrada ? "Lugar donde la encontraste" : "Lugar de la última vez vista"}</legend><div className="person-map"><MapaUbicacion latitud={datos.latEvento} longitud={datos.lngEvento} precision={datos.precisionGps} onLocation={seleccionarPunto} etiqueta="Mapa para seleccionar la ubicación" /></div><input type="hidden" value={datos.latEvento ?? ""} readOnly /><input type="hidden" value={datos.lngEvento ?? ""} readOnly /><button className="person-current-location" type="button" onClick={usarUbicacionActual} disabled={estadoGps === "cargando"}><CrosshairIcon weight="fill" />{estadoGps === "cargando" ? "Obteniendo ubicación…" : "Usar mi ubicación actual"}</button>{mensajeGps && <p className={`gps-message gps-message--${estadoGps}`} role="status">{estadoGps === "error" ? <WarningCircleIcon weight="bold" /> : <MapPinIcon weight="fill" />}{mensajeGps}</p>}</fieldset>

        <label>Dirección o referencia<textarea rows="3" maxLength="150" value={datos.direccionEvento} onChange={(evento) => onChange((actuales) => ({ ...actuales, direccionEvento: sanitizarTexto(evento.target.value, { recortar: false }) }))} placeholder="Ej: Cerca del centro comercial, estación de metro, parque…" /><small>{datos.direccionEvento.length}/150</small></label>

        <AvisoDireccionDesalineada
          textoActual={datos.direccionEvento}
          textoGeocodificado={datos.direccionGeocodificada}
          ciudad={datos.ciudad}
          onUbicar={(encontrada) => {
            guardarPunto({ latitud: encontrada.latitud, longitud: encontrada.longitud, precision: null });
            onChange((actuales) => ({
              ...actuales,
              direccionEvento: encontrada.direccion.slice(0, 150),
              direccionGeocodificada: encontrada.direccion.slice(0, 150),
              ciudad: encontrada.ciudad || null,
              departamento: encontrada.departamento || null,
            }));
          }}
        />

        <label>Punto de referencia <span>(opcional)</span><textarea rows="3" maxLength="150" value={datos.puntoReferencia} onChange={(evento) => onChange((actuales) => ({ ...actuales, puntoReferencia: sanitizarTexto(evento.target.value, { recortar: false }) }))} placeholder="Ej: al lado de la farmacia, cerca del semáforo…" /><small>{datos.puntoReferencia.length}/150</small></label>

        <fieldset className="date-time-fields person-date-time"><legend>Fecha y hora aproximada</legend><label><CalendarBlankIcon weight="bold" /><span>Fecha<input type="date" max={datos.fechaMaxima} value={datos.fechaEvento} onChange={(evento) => onChange((actuales) => ({ ...actuales, fechaEvento: evento.target.value }))} /></span></label><label><ClockIcon weight="bold" /><span>Hora<input type="time" value={datos.horaEvento} onChange={(evento) => onChange((actuales) => ({ ...actuales, horaEvento: evento.target.value }))} /></span></label></fieldset>

        <div className="person-location-actions"><button type="button" onClick={onBack}><ArrowLeftIcon weight="bold" />Atrás</button><button type="submit">Siguiente<ArrowRightIcon weight="bold" /></button></div>
      </form>
    </section>
  );
}
